import Pill, { PillProps } from 'ext-ui/components/Pill/Pill'
import React, { useEffect, useId, useRef, useState } from 'react'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import * as style from 'src/app/CardFeedDetails/components/CardFeedDetailsHeaderTitle.style'
import HoverTooltip from 'src/components/HoverTooltip/HoverTooltip'
import SvgPencilIcon from 'src/components/Svgs/SvgPencilIcon'
import { useTranslate } from 'src/localization/Trns'
import { CardFeedStatus } from 'src/queries/cardFeeds/cardFeeds.models'
import { useUpdateCardFeed } from 'src/queries/cardFeeds/domain/cardFeeds.queries'

type NonConnectedStatus = Exclude<CardFeedStatus, 'ACTIVE'>

// Matches the backend @Size(max = 200) on UpdateCardFeedRequest.cardProgramName.
const CARD_FEED_NAME_MAX_LENGTH = 200

// Localized status copy for feeds that are not connected; keys exist in
// en-US.json.
const STATUS_LABEL: Record<NonConnectedStatus, string> = {
  SUBMITTED: 'Pending',
  FAILED: 'Connection Failed',
  DEACTIVATED: 'Disconnected',
}

// undefined → the Pill's default grey.
const STATUS_PILL_COLOR: Record<NonConnectedStatus, PillProps['color']> = {
  SUBMITTED: 'pending',
  FAILED: 'severe',
  DEACTIVATED: undefined,
}

interface CardFeedDetailsHeaderTitleProps {
  name: string
  status: CardFeedStatus
  // The API may omit the reference id; the ID tag is hidden when it's absent.
  referenceId: string | null | undefined
  cardFeedId: string
  // Needed for the PATCH; omitted only if the session has no org, in which case
  // editing is disabled.
  organizationId: string | undefined
}

const CardFeedDetailsHeaderTitle: React.FC<CardFeedDetailsHeaderTitleProps> = ({
  name,
  status,
  referenceId,
  cardFeedId,
  organizationId,
}) => {
  const translate = useTranslate()
  const { analytics } = useAnalyticsContext()
  const isConnected = status === 'ACTIVE'
  // Editing needs the owning org for the PATCH, so it's only offered once the
  // session has resolved an org id. The backend allows PATCH in any feed state.
  const canEdit = organizationId !== undefined

  const [isEditing, setIsEditing] = useState(false)
  const [draft, setDraft] = useState(name)
  const [hasError, setHasError] = useState(false)
  // Drives the hover/focus tooltip on the (non-editing) title.
  const [showTooltip, setShowTooltip] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  // Set when the editor is closed via the keyboard (Enter/Escape) so we can
  // return focus to the trigger after it remounts (WCAG 2.4.3). Left false for
  // blur-driven closes, where focus is already moving to wherever the user
  // clicked and stealing it back would be wrong.
  const restoreFocusRef = useRef(false)
  // Associates the input with its helper/error text for screen readers.
  const helperId = useId()

  const { mutate: updateCardFeed, isPending } = useUpdateCardFeed({
    onSuccess: () => {
      analytics.track('card feed details', { action: 'renamed' })
      setHasError(false)
      setIsEditing(false)
    },
    onError: () => {
      // Keep the editor open so the user can retry or correct the name.
      setHasError(true)
    },
  })

  // When the editor opens, focus and select the field so the user can type or
  // overwrite immediately. When it closes from a keyboard action, return focus
  // to the trigger so the user keeps their place in the page (WCAG 2.4.3).
  useEffect(() => {
    if (isEditing) {
      inputRef.current?.focus()
      inputRef.current?.select()
    } else if (restoreFocusRef.current) {
      restoreFocusRef.current = false
      triggerRef.current?.focus()
    }
  }, [isEditing])

  const openEditor = (): void => {
    setDraft(name)
    setHasError(false)
    // Clicking unmounts the trigger before onMouseLeave/onBlur can fire, so
    // clear the tooltip here or it lingers when the trigger remounts after edit.
    setShowTooltip(false)
    restoreFocusRef.current = false
    setIsEditing(true)
  }

  const closeEditor = (): void => {
    setHasError(false)
    setIsEditing(false)
  }

  const save = (): void => {
    if (isPending) return
    const trimmed = draft.trim()
    // A blank name is rejected by the backend (@NotBlank) and an unchanged name
    // is a server-side no-op, so skip the request and just close the editor.
    if (trimmed.length === 0 || trimmed === name) {
      closeEditor()
      return
    }
    if (organizationId === undefined) return
    updateCardFeed({
      organizationId,
      cardFeedId,
      body: { cardProgramName: trimmed },
    })
  }

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ): void => {
    if (event.key === 'Enter') {
      event.preventDefault()
      // Keyboard-driven close: return focus to the trigger once it remounts.
      // For a successful save the editor closes asynchronously in onSuccess;
      // for a blank/unchanged name save() closes it synchronously.
      restoreFocusRef.current = true
      save()
    } else if (event.key === 'Escape') {
      event.preventDefault()
      restoreFocusRef.current = true
      closeEditor()
    }
  }

  const handleBlur = (): void => {
    // Don't discard the in-flight edit while a save is pending; the success or
    // error handler closes the editor.
    if (!isPending) closeEditor()
  }

  // Size the field to its text (in character widths) so it doesn't stretch the
  // full page width; a small floor keeps a short/empty name usable.
  const inputSize = Math.max(draft.length + 1, 8)

  // The backend lifecycle can outgrow the statuses the frontend knows. An
  // unmapped status would otherwise feed `undefined` into translate() (which
  // crashes on a missing key), so resolve the label/color up front and hide the
  // pill entirely when we don't recognize the status.
  const statusLabel: string | undefined =
    STATUS_LABEL[status as NonConnectedStatus]
  const statusPillColor = STATUS_PILL_COLOR[status as NonConnectedStatus]

  return (
    <span css={style.title}>
      {isEditing ? (
        // Keep the pencil visible while editing so the title doesn't lose its
        // affordance mid-edit, matching the reimbursement report name field.
        <span css={style.editRow}>
          <input
            ref={inputRef}
            type="text"
            value={draft}
            size={inputSize}
            maxLength={CARD_FEED_NAME_MAX_LENGTH}
            disabled={isPending}
            aria-label={translate('Card feed name')}
            aria-invalid={hasError}
            aria-describedby={helperId}
            onChange={(event): void => setDraft(event.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            data-testid="card-feed-details-name-input"
            css={style.nameInput(hasError)}
          />
          <SvgPencilIcon
            width={20}
            height={20}
            viewBox="0 0 16 16"
            css={style.editIcon}
          />
        </span>
      ) : canEdit ? (
        // The name itself is the edit affordance: hovering/focusing reveals the
        // tooltip and clicking anywhere on the title (name or pencil) opens the
        // editor — mirroring the reimbursement report details name field.
        <span css={style.editTriggerWrap}>
          <button
            ref={triggerRef}
            type="button"
            onClick={openEditor}
            onMouseEnter={(): void => setShowTooltip(true)}
            onMouseLeave={(): void => setShowTooltip(false)}
            onFocus={(): void => setShowTooltip(true)}
            onBlur={(): void => setShowTooltip(false)}
            // No aria-label: the button is named by the feed name it contains,
            // so the surrounding <h1> keeps the name as its accessible name (and
            // we satisfy WCAG 2.5.3). The pencil + hover tooltip carry the visual
            // edit affordance — matching the reimbursement report name field.
            data-testid="card-feed-details-edit"
            css={style.editTrigger}
          >
            <span>{name}</span>
            <SvgPencilIcon
              width={20}
              height={20}
              viewBox="0 0 16 16"
              css={style.editIcon}
            />
          </button>
          {/* Rendered as a sibling, not a child of the button, so its text
              never joins the button's (or the <h1>'s) accessible name — even
              while shown on focus. */}
          <HoverTooltip
            show={showTooltip}
            tooltipText={translate('Edit Card Feed Name')}
          />
        </span>
      ) : (
        <span>{name}</span>
      )}
      {isConnected
        ? referenceId && (
            <Pill data-testid="card-feed-details-id">
              {translate('ID: ${id}', { id: referenceId })}
            </Pill>
          )
        : statusLabel && (
            <Pill
              color={statusPillColor}
              data-testid="card-feed-details-status"
            >
              {translate(statusLabel)}
            </Pill>
          )}
      {isEditing && (
        <span
          id={helperId}
          role={hasError ? 'alert' : undefined}
          css={style.helperText(hasError)}
          data-testid="card-feed-details-name-helper"
        >
          {hasError
            ? translate('We couldn’t rename this card feed. Please try again.')
            : translate('Press Enter to save')}
        </span>
      )}
    </span>
  )
}

export default CardFeedDetailsHeaderTitle
